import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Container,
  ListItemText,
  ListItem,
  List,
  ListItemIcon,
  useTheme
} from '@mui/material';
import { 
  Description, 
  Assignment, 
  CheckCircle, 
  ArrowBack,
  Timeline,
  AccountTree,
  Save
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';

const AssignWorkflow = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  
  // States
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [document, setDocument] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  
  // Fetch document data and workflow templates
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (!user || !user.token) {
          throw new Error('Authentication token not found');
        }
        
        // Fetch document details
        const documentResponse = await axios.get(
          `${API_BASE_URL}/documents/${documentId}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        
        setDocument(documentResponse.data);
        
        // Set default workflow name based on document title
        if (documentResponse.data?.title) {
          setWorkflowName(`${documentResponse.data.title} - Workflow`);
        }
        
        // Fetch workflow templates
        const templatesResponse = await axios.get(
          `${API_BASE_URL}/workflow-templates`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        
        if (templatesResponse.data) {
          const templatesData = Array.isArray(templatesResponse.data) 
            ? templatesResponse.data 
            : templatesResponse.data.data || [];
            
          setTemplates(templatesData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.response?.data?.message || error.message || 'Failed to load data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [documentId, user]);
  
  // Handle template selection
  const handleTemplateChange = async (event) => {
    const templateId = event.target.value;
    setSelectedTemplate(templateId);
    
    // Find the selected template
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setWorkflowDescription(template.description || '');
      
      try {
        // Fetch template details with steps
        const response = await axios.get(
          `${API_BASE_URL}/workflow-templates/${templateId}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        
        setTemplateDetails(response.data);
      } catch (error) {
        console.error(`Error fetching template details for ${templateId}:`, error);
        // Don't set error state here, just log it
      }
    } else {
      setTemplateDetails(null);
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (!selectedTemplate) {
        setError('Please select a workflow template');
        return;
      }
      
      setSubmitting(true);
      setError(null);
      
      if (!user || !user.token) {
        throw new Error('Authentication token not found');
      }
      
      // Create workflow data - use startWorkflow endpoint
      const workflowData = {
        documentId: documentId,
        templateId: selectedTemplate,
        name: workflowName,
        description: workflowDescription,
        assignees: [] // Add empty assignees array
      };
      
      console.log('Submitting workflow data:', workflowData);
      
      // First try using the workflow controller's startWorkflow endpoint
      let response;
      try {
        response = await axios.post(
          `${API_BASE_URL}/workflows/start`,
          workflowData,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } catch (error) {
        console.log("First method failed, trying fallback...", error);
        
        // If that fails, try the document workflow endpoint with the template_id
        // Also include steps with order_number if template details are available
        let stepsData = [];
      
        if (templateDetails && templateDetails.steps && templateDetails.steps.length > 0) {
          stepsData = templateDetails.steps.map((step, index) => ({
            name: step.name,
            description: step.description || '',
            order_number: step.order_number || index + 1,
            assigned_to_user_id: step.assigned_to_user_id || null,
            assigned_to_role_id: step.assigned_to_role_id || null
          }));
        }
        
        response = await axios.post(
          `${API_BASE_URL}/documents/${documentId}/workflow`,
          {
            name: workflowName,
            description: workflowDescription,
            template_id: selectedTemplate,
            steps: stepsData
          },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      
      setSuccess('Workflow assigned successfully');
      toast.success('Workflow assigned successfully');
      
      // Move to next step
      setActiveStep(1);
      
      // Navigate to workflow page after delay
      setTimeout(() => {
        navigate(`/documents/${documentId}`);
      }, 2000);
      
      setSubmitting(false);
    } catch (error) {
      console.error('Error assigning workflow:', error);
      
      // Special handling for "workflow already exists" case
      if (error.response?.data?.workflow_id) {
        const existingWorkflowId = error.response.data.workflow_id;
        setError(
          <Box>
            {error.response.data.message}
            <Button 
              variant="contained"
              color="primary"
              size="small"
              sx={{ ml: 2 }}
              onClick={() => navigate(`/documents/${documentId}/workflow`)}
            >
              View Existing Workflow
            </Button>
          </Box>
        );
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to assign workflow');
      }
      
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!document) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
        Document not found or could not be loaded. Please check document ID: {documentId}
      </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/documents')}
        >
          Back to Documents
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/documents/${documentId}`)}
          sx={{ mr: 2 }}
        >
          Back to Document
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1, fontWeight: 500 }}>
          Assign Workflow
              </Typography>
      </Box>
          
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Description color="primary" sx={{ mr: 1.5 }} />
          <Box>
            <Typography variant="h6">
              {document.title}
            </Typography>
            {document.reference_number && (
              <Typography variant="body2" color="text.secondary">
                Reference: {document.reference_number}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
      
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: 4,
              '& .MuiStepLabel-iconContainer': {
                '& .MuiStepIcon-root.Mui-active': {
                  color: theme.palette.primary.main,
                },
                '& .MuiStepIcon-root.Mui-completed': {
                  color: theme.palette.success.main,
                },
              }
            }}
          >
            <Step>
              <StepLabel>Select Template</StepLabel>
            </Step>
            <Step>
              <StepLabel>Workflow Created</StepLabel>
            </Step>
          </Stepper>
          
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <AccountTree sx={{ mr: 1.5 }} />
                Workflow Template Selection
              </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="template-select-label">Workflow Template</InputLabel>
                  <Select
                      labelId="template-select-label"
                      id="template-select"
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    label="Workflow Template"
                    disabled={submitting}
                  >
                    <MenuItem value="">
                      <em>Select a template</em>
                    </MenuItem>
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Workflow Name"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  fullWidth
                  required
                  disabled={submitting}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Workflow Description"
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  disabled={submitting}
                />
              </Grid>
                
                {templateDetails && templateDetails.steps && templateDetails.steps.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                      <Timeline sx={{ mr: 1 }} />
                      Template Steps
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                      <List>
                        {templateDetails.steps.map((step, index) => (
                          <ListItem key={step.id} sx={{ py: 1 }}>
                            <ListItemIcon>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  fontWeight: 'bold',
                                }}
                              >
                                {index + 1}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={step.name}
                              secondary={step.description}
                              primaryTypographyProps={{ fontWeight: 'medium' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                )}
              
              <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/documents/${documentId}`)}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={submitting || !selectedTemplate || !workflowName}
                      startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                      sx={{ px: 3 }}
                >
                      Assign Workflow
                </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle color="success" sx={{ fontSize: 80, mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Workflow Successfully Assigned
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                The workflow has been successfully assigned to the document.
                You will be redirected to the document page shortly.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate(`/documents/${documentId}`)}
                sx={{ px: 3, py: 1 }}
              >
                Return to Document
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AssignWorkflow; 